// ── Head-office admin dashboard ──
// Shows total stokvels & members, referral partners with their once-off R100/stokvel
// incentive owed (per stokvel that has made its initial payment), a "record payment"
// action, and full payout history. Access is enforced server-side (admin allowlist);
// non-admins simply get a 403 here. Reachable at ?headoffice=1 while signed in.
import React, { useCallback, useEffect, useState } from 'react';
import { adminGetDashboard, adminSaveReferral, adminRecordPayout } from './adminApi';

const C = { navy: '#16233b', gold: '#c79a3a', line: '#e2e6ec', muted: '#6b7480', green: '#2f7d4f', bg: '#f4f6f9' };
const rand = (n) => 'R' + (Number(n) || 0).toLocaleString('en-ZA');
const card = { background: '#fff', border: '1px solid ' + C.line, borderRadius: 10, padding: '14px 16px' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: C.muted, borderBottom: '2px solid ' + C.line, textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '8px 10px', fontSize: 12.5, borderBottom: '1px solid ' + C.line, verticalAlign: 'middle' };
const inp = { padding: '8px 10px', border: '1px solid ' + C.line, borderRadius: 6, fontSize: 13, outline: 'none' };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' });

export default function AdminDashboard({ embedded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ code: '', partnerName: '', contactEmail: '', amountPerStokvel: '100' });

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await adminGetDashboard()); }
    catch (e) { setErr(e.message || 'Could not load the dashboard.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveReferral = async (e) => {
    if (e) e.preventDefault();
    if (!form.code.trim()) { setErr('Enter a referral code.'); return; }
    setBusy('save');
    try { await adminSaveReferral(form); setForm({ code: '', partnerName: '', contactEmail: '', amountPerStokvel: '100' }); await load(); }
    catch (e2) { alert(e2.message || 'Could not save.'); }
    setBusy('');
  };

  const payNow = async (r) => {
    if (!r.owedStokvels) return;
    if (!window.confirm(`Record a payment of ${rand(r.owedAmount)} to ${r.partnerName || r.code} for ${r.owedStokvels} stokvel(s) that have paid?\n\nThis marks those stokvels as paid so they won't be paid again.`)) return;
    setBusy('pay' + r.code);
    try { await adminRecordPayout({ code: r.code, stokvelIds: r.owedIds }); await load(); }
    catch (e) { alert(e.message || 'Could not record payment.'); }
    setBusy('');
  };

  const editReferral = (r) => setForm({ code: r.code, partnerName: r.partnerName === '(unregistered — set up this partner)' ? '' : r.partnerName, contactEmail: r.contactEmail || '', amountPerStokvel: String(r.amountPerStokvel != null ? r.amountPerStokvel : 100) });

  if (loading) return <Shell embedded={embedded}><div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading head-office dashboard…</div></Shell>;
  if (err) return <Shell embedded={embedded}><div style={{ ...card, borderColor: '#f0c0c0', color: '#a33' }}>{err}{String(err).includes('403') || /head-office/i.test(err) ? ' — this account is not on the head-office allowlist.' : ''}</div></Shell>;

  const t = data.totals || {};
  const totalOwed = (data.referrals || []).reduce((a, r) => a + (r.owedAmount || 0), 0);
  const totalPaid = (data.referrals || []).reduce((a, r) => a + (r.totalPaidAllTime || 0), 0);

  return (
    <Shell onRefresh={load} embedded={embedded}>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Kpi label="Stokvels joined" value={t.stokvels} />
        <Kpi label="Total members" value={t.members} />
        <Kpi label="Paying stokvels" value={t.payingStokvels || 0} />
        <Kpi label="Monthly recurring revenue" value={rand(t.mrr)} accent={C.green} />
        <Kpi label="Revenue (all time)" value={rand(t.revenueTotal)} accent={C.green} />
        <Kpi label="Owed to partners now" value={rand(totalOwed)} accent={C.gold} />
        <Kpi label="Paid to partners (all time)" value={rand(totalPaid)} accent={C.green} />
      </div>

      {/* Revenue */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Tlhakiso subscription revenue per month</div>
        <RevenueChart payments={data.revenuePayments || []} />
      </div>

      {/* Monthly growth */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Stokvels joining per month</div>
        <GrowthChart timestamps={(data.stokvels || []).map((s) => s.createdAt).filter(Boolean)} noun="stokvel" />
      </div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Members joining per month</div>
        <GrowthChart timestamps={data.memberJoins || []} noun="member" />
      </div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Referred sign-ups per month</div>
        <GrowthChart timestamps={data.referralJoins || []} noun="referral" />
      </div>

      {/* Referral partners */}
      <h2 style={{ color: C.navy, fontSize: 15, margin: '18px 0 8px' }}>Referral partners</h2>
      <form onSubmit={saveReferral} style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <input style={{ ...inp, width: 110, fontFamily: 'monospace', textTransform: 'uppercase' }} placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        <input style={{ ...inp, flex: 1, minWidth: 160 }} placeholder="Partner name (e.g. Women in Stokvels)" value={form.partnerName} onChange={(e) => setForm({ ...form, partnerName: e.target.value })} />
        <input style={{ ...inp, width: 200 }} placeholder="Contact email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
        <span style={{ fontSize: 12, color: C.muted }}>R</span>
        <input style={{ ...inp, width: 70 }} type="number" placeholder="100" value={form.amountPerStokvel} onChange={(e) => setForm({ ...form, amountPerStokvel: e.target.value })} title="Once-off amount per referred stokvel that pays" />
        <button type="submit" disabled={busy === 'save'} style={btn(C.navy)}>{busy === 'save' ? 'Saving…' : 'Add / update partner'}</button>
      </form>

      <div style={{ ...card, overflowX: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Code</th><th style={th}>Partner</th><th style={{ ...th, textAlign: 'right' }}>R / stokvel</th>
            <th style={{ ...th, textAlign: 'right' }}>Referred</th><th style={{ ...th, textAlign: 'right' }}>Paid initial</th>
            <th style={{ ...th, textAlign: 'right' }}>Owed now</th><th style={{ ...th, textAlign: 'right' }}>Paid to date</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {(data.referrals || []).length === 0 && <tr><td style={{ ...td, color: C.muted }} colSpan={8}>No referrals yet. Give a partner a code (e.g. WIS) and share <code>?ref=WIS</code>.</td></tr>}
            {(data.referrals || []).map((r) => (
              <tr key={r.code}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{r.code}</td>
                <td style={td}>{r.partnerName || '—'}{r.unregistered && <span style={{ color: '#b5651d', fontSize: 10.5 }}> ⚠ set up</span>}{r.active === false && <span style={{ color: C.muted, fontSize: 10.5 }}> (inactive)</span>}<div style={{ fontSize: 10.5, color: C.muted }}>{r.contactEmail}</div></td>
                <td style={{ ...td, textAlign: 'right' }}>{rand(r.amountPerStokvel)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.referredStokvels}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.qualifiedStokvels}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: r.owedAmount ? C.gold : C.muted }}>{rand(r.owedAmount)}<div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>{r.owedStokvels} stokvel(s)</div></td>
                <td style={{ ...td, textAlign: 'right', color: C.green }}>{rand(r.totalPaidAllTime)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {r.owedStokvels > 0 && <button onClick={() => payNow(r)} disabled={!!busy} style={{ ...btn(C.green), marginRight: 6 }}>{busy === 'pay' + r.code ? '…' : 'Pay ' + rand(r.owedAmount)}</button>}
                  <button onClick={() => editReferral(r)} style={{ background: 'none', border: '1px solid ' + C.line, borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: C.navy }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment history */}
      <h2 style={{ color: C.navy, fontSize: 15, margin: '22px 0 8px' }}>Payment history</h2>
      <div style={{ ...card, overflowX: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Date</th><th style={th}>Code</th><th style={th}>Partner</th><th style={{ ...th, textAlign: 'right' }}>Stokvels</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Recorded by</th></tr></thead>
          <tbody>
            {(data.payouts || []).length === 0 && <tr><td style={{ ...td, color: C.muted }} colSpan={6}>No payments recorded yet.</td></tr>}
            {(data.payouts || []).map((p) => (
              <tr key={p.id}>
                <td style={td}>{p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{p.code}</td>
                <td style={td}>{p.partnerName || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{p.stokvelCount}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{rand(p.amount)}</td>
                <td style={{ ...td, fontSize: 11, color: C.muted }}>{p.recordedBy || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stokvels */}
      <h2 style={{ color: C.navy, fontSize: 15, margin: '22px 0 8px' }}>Stokvels ({(data.stokvels || []).length})</h2>
      <div style={{ ...card, overflowX: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Name</th><th style={th}>Type</th><th style={th}>Created</th><th style={{ ...th, textAlign: 'right' }}>Members</th><th style={th}>Subscription</th><th style={th}>Paid?</th><th style={th}>Referral</th></tr></thead>
          <tbody>
            {(data.stokvels || []).map((s) => (
              <tr key={s.id}>
                <td style={td}>{s.name || '—'}</td>
                <td style={{ ...td, textTransform: 'capitalize' }}>{s.type || '—'}</td>
                <td style={td}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{s.members}</td>
                <td style={td}>{s.subscription}</td>
                <td style={td}>{s.hasPaid ? <span style={{ color: C.green, fontWeight: 700 }}>✓</span> : <span style={{ color: C.muted }}>—</span>}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{s.referral || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || C.navy }}>{value != null ? value : '—'}</div>
      <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>{label}</div>
    </div>
  );
}

// Count of stokvels created per calendar month, gap-filled from first to last month.
function monthlySeries(timestamps) {
  const counts = {}; let min = null, max = null;
  (timestamps || []).forEach((ms) => {
    if (!ms) return;
    const d = new Date(ms);
    const ym = d.getFullYear() * 12 + d.getMonth();
    const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    counts[key] = (counts[key] || 0) + 1;
    if (min === null || ym < min) min = ym;
    if (max === null || ym > max) max = ym;
  });
  if (min === null) return [];
  const out = [];
  for (let ym = min; ym <= max; ym++) {
    const y = Math.floor(ym / 12), m = ym % 12;
    const key = y + '-' + String(m + 1).padStart(2, '0');
    out.push({ key, label: new Date(y, m, 1).toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' }), value: counts[key] || 0 });
  }
  return out;
}

// Time window (ms) for a range filter. "last N months" spans N calendar months
// including the current one; "older" is anything before the last 12 months.
function rangeWindow(range, from, to) {
  const now = new Date();
  const end = now.getTime();
  if (range === 'this') return [new Date(now.getFullYear(), now.getMonth(), 1).getTime(), end];
  if (range === '3m' || range === '6m' || range === '12m') {
    const n = range === '3m' ? 3 : range === '6m' ? 6 : 12;
    return [new Date(now.getFullYear(), now.getMonth() - (n - 1), 1).getTime(), end];
  }
  if (range === 'older') return [0, new Date(now.getFullYear(), now.getMonth() - 11, 1).getTime() - 1];
  if (range === 'custom') return [from ? new Date(from).getTime() : 0, to ? new Date(to).getTime() + 86400000 - 1 : end];
  return [0, end];
}

const GROWTH_RANGES = [['this', 'This month'], ['3m', 'Last 3 months'], ['6m', 'Last 6 months'], ['12m', 'Last 12 months'], ['older', 'Older'], ['custom', 'Custom']];

// Filterable monthly-growth chart driven by an array of ms timestamps.
function GrowthChart({ timestamps, noun }) {
  const [range, setRange] = useState('12m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [s, e] = rangeWindow(range, from, to);
  const filtered = (timestamps || []).filter((ms) => ms && ms >= s && ms <= e);
  const total = filtered.length;
  const btnStyle = (active) => ({ fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid ' + (active ? C.navy : C.line), background: active ? C.navy : '#fff', color: active ? '#fff' : C.navy });
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {GROWTH_RANGES.map(([id, label]) => <button key={id} onClick={() => setRange(id)} style={btnStyle(range === id)}>{label}</button>)}
      </div>
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, color: C.muted }}>From</label>
          <input type="date" value={from} onChange={(ev) => setFrom(ev.target.value)} style={{ ...inp, width: 150 }} />
          <label style={{ fontSize: 11, color: C.muted }}>To</label>
          <input type="date" value={to} onChange={(ev) => setTo(ev.target.value)} style={{ ...inp, width: 150 }} />
        </div>
      )}
      <div style={{ fontSize: 11.5, color: C.muted, margin: '8px 0 2px' }}><strong style={{ color: C.navy }}>{total}</strong> {noun || 'item'}{total === 1 ? '' : 's'} in this period</div>
      <LineChart series={monthlySeries(filtered)} />
    </div>
  );
}

// Monthly revenue series (sum of payment amounts per calendar month), gap-filled.
function monthlyRevenue(payments) {
  const sums = {}; let min = null, max = null;
  (payments || []).forEach((p) => {
    if (!p || !p.at) return;
    const d = new Date(p.at);
    const ym = d.getFullYear() * 12 + d.getMonth();
    const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    sums[key] = (sums[key] || 0) + (Number(p.amount) || 0);
    if (min === null || ym < min) min = ym;
    if (max === null || ym > max) max = ym;
  });
  if (min === null) return [];
  const out = [];
  for (let ym = min; ym <= max; ym++) {
    const y = Math.floor(ym / 12), m = ym % 12;
    const key = y + '-' + String(m + 1).padStart(2, '0');
    out.push({ key, label: new Date(y, m, 1).toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' }), value: sums[key] || 0 });
  }
  return out;
}

// Filterable monthly-revenue chart (Tlhakiso subscription income), Rand-formatted.
function RevenueChart({ payments }) {
  const [range, setRange] = useState('12m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [s, e] = rangeWindow(range, from, to);
  const filtered = (payments || []).filter((p) => p && p.at && p.at >= s && p.at <= e);
  const total = filtered.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const btnStyle = (active) => ({ fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid ' + (active ? C.navy : C.line), background: active ? C.navy : '#fff', color: active ? '#fff' : C.navy });
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {GROWTH_RANGES.map(([id, label]) => <button key={id} onClick={() => setRange(id)} style={btnStyle(range === id)}>{label}</button>)}
      </div>
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, color: C.muted }}>From</label>
          <input type="date" value={from} onChange={(ev) => setFrom(ev.target.value)} style={{ ...inp, width: 150 }} />
          <label style={{ fontSize: 11, color: C.muted }}>To</label>
          <input type="date" value={to} onChange={(ev) => setTo(ev.target.value)} style={{ ...inp, width: 150 }} />
        </div>
      )}
      <div style={{ fontSize: 11.5, color: C.muted, margin: '8px 0 2px' }}><strong style={{ color: C.green }}>{rand(total)}</strong> collected in this period</div>
      <LineChart series={monthlyRevenue(filtered)} fmt={rand} emptyMsg="No subscription payments recorded in this period yet." />
    </div>
  );
}

// Lightweight inline SVG line chart — no external chart library (CSP-safe, works in-app).
function LineChart({ series, fmt, emptyMsg }) {
  if (!series || !series.length) return <div style={{ color: C.muted, fontSize: 12, padding: '10px 0' }}>{emptyMsg || 'No data in this period yet.'}</div>;
  const f = fmt || ((v) => v);
  const W = Math.max(340, series.length * 64), H = 190, pad = { l: fmt ? 46 : 30, r: 14, t: 16, b: 30 };
  const maxV = Math.max(1, ...series.map((s) => s.value));
  const x = (i) => pad.l + (series.length === 1 ? (W - pad.l - pad.r) / 2 : (i * (W - pad.l - pad.r)) / (series.length - 1));
  const y = (v) => H - pad.b - (v * (H - pad.t - pad.b)) / maxV;
  const pts = series.map((s, i) => [x(i), y(s.value)]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', minWidth: Math.min(W, 340), maxWidth: W, height: 'auto' }}>
        {[0, maxV].map((v, i) => (
          <g key={i}>
            <line x1={pad.l} y1={y(v)} x2={W - pad.r} y2={y(v)} stroke="#eceef2" />
            <text x={pad.l - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill={C.muted}>{f(v)}</text>
          </g>
        ))}
        <path d={line} fill="none" stroke={C.gold} strokeWidth="2.5" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="3.5" fill={C.navy} />
            <text x={p[0]} y={p[1] - 8} textAnchor="middle" fontSize="9" fontWeight="700" fill={C.navy}>{f(series[i].value)}</text>
            <text x={p[0]} y={H - 10} textAnchor="middle" fontSize="8.5" fill={C.muted}>{series[i].label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function Shell({ children, onRefresh, embedded }) {
  if (embedded) {
    return (
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div><span style={{ fontWeight: 800, fontSize: 17, color: C.navy }}>Head Office</span> <span style={{ color: C.muted, fontSize: 12.5 }}>— referrals &amp; stokvels</span></div>
          {onRefresh && <button onClick={onRefresh} style={btn(C.navy)}>Refresh</button>}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background: C.navy, color: '#fff', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><span style={{ fontWeight: 800, fontSize: 16 }}>Tlhakiso</span> <span style={{ color: C.gold, fontWeight: 700 }}>Head Office</span></div>
        <div style={{ display: 'flex', gap: 10 }}>
          {onRefresh && <button onClick={onRefresh} style={{ ...btn('rgba(255,255,255,0.15)') }}>Refresh</button>}
          <a href="/" style={{ ...btn('rgba(255,255,255,0.15)'), textDecoration: 'none', display: 'inline-block' }}>Exit</a>
        </div>
      </div>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 22px 60px' }}>{children}</div>
    </div>
  );
}
